/* eslint-disable import/no-cycle */
import React, { useState, createContext, useContext, useCallback, useMemo, useEffect, type ReactNode } from 'react'
import { NotFound, Route, useQueryParams, useHistory, useLocation } from '@fs/zion-router'
import { i18n } from '@fs/zion-locale'
import usePlaceReportsIndex from './hooks/usePlaceReportsIndex'
import { type PlaceReportsState, type Region, type Country, type Division } from './types'

const PlaceReportsAppContext = createContext<PlaceReportsState | undefined>(undefined)

function checkScorecardAvailability(reports: string[] = []): boolean {
  return reports.some((file) => file.startsWith('score_breakdown_'))
}

// Helper function to find country by ID within a region
function findCountryInRegion(region: Region, countryId: string | number): Country | undefined {
  return (region?.countries || []).find((c) => Number(c.id) === Number(countryId))
}

// Helper function to find division by ID within a country
function findDivisionInCountry(country: Country, divisionId: string | number): Division | undefined {
  return (country?.divisions || []).find((d) => Number(d.id) === Number(divisionId))
}

type PlaceReportsProviderProps = Readonly<{
  children: ReactNode
  placeManagerState: Partial<PlaceReportsState>
}>

export default function PlaceReportsPageProvider({
  children,
  placeManagerState,
}: PlaceReportsProviderProps): JSX.Element {
  const { query } = useQueryParams()
  const history = useHistory()
  const location = useLocation()
  const initialViewType: 'guidelines' | 'scorecard' = query.view === 'scorecard' ? 'scorecard' : 'guidelines'

  const [locale, setLocale] = useState(i18n.language || 'en')
  const [selectedLocale, setSelectedLocale] = useState(i18n.language || 'en')
  const [viewType, setViewType] = useState<'guidelines' | 'scorecard'>(initialViewType)
  const { reportsIndex, setReportsIndex, error: reportsIndexError } = usePlaceReportsIndex()

  // Sync viewType state with URL changes (e.g., browser back/forward)
  useEffect(() => {
    const urlViewType: 'guidelines' | 'scorecard' = query.view === 'scorecard' ? 'scorecard' : 'guidelines'
    if (urlViewType !== viewType) {
      setViewType(urlViewType)
    }
  }, [query.view, viewType])

  const getRegion = useCallback(
    (id: string | number): Region | undefined => {
      const report = (reportsIndex?.regions || []).find((region) => Number(region.id) === Number(id))
      return report
    },
    [reportsIndex]
  )

  const getCountryAndRegion = useCallback(
    (id: string | number): { region: Region | undefined; country: Country | null } => {
      let country: Country | null = null
      const region = (reportsIndex?.regions || []).find((r) => {
        const foundCountry = findCountryInRegion(r, id)
        if (foundCountry) {
          country = foundCountry
        }
        return foundCountry
      })
      return { region, country }
    },
    [reportsIndex]
  )

  const getDivision = useCallback(
    (id: string | number): { region: Region | undefined; country: Country | undefined; division: Division | null } => {
      let division: Division | null = null
      let country: Country | undefined
      const region = (reportsIndex?.regions || []).find((r) => {
        const foundCountry = (r?.countries || []).find((c) => {
          const foundDivision = findDivisionInCountry(c, id)
          if (foundDivision) {
            division = foundDivision
          }
          return foundDivision
        })
        if (foundCountry) {
          country = foundCountry
        }
        return foundCountry
      })
      return { region, country, division }
    },
    [reportsIndex]
  )

  const countries = useMemo(() => {
    const allCountries: Country[] = []
    reportsIndex?.regions?.forEach((r) => {
      allCountries.push(...(r?.countries || []))
    })
    return allCountries
  }, [reportsIndex])

  // Calculate scorecard availability based on current route
  const hasScorecardAvailable = useMemo(() => {
    const { region, country, division } = query
    let reports: string[] = []

    if (region && country && division) {
      reports = getDivision(division)?.division?.reports || []
    } else if (region && country) {
      reports = getCountryAndRegion(country)?.country?.reports || []
    } else if (region) {
      reports = getRegion(region)?.reports || []
    }

    return checkScorecardAvailability(reports)
  }, [query, getRegion, getCountryAndRegion, getDivision])

  // Auto-fallback: Clear scorecard URL param when scorecard is not available
  useEffect(() => {
    if (query.view === 'scorecard' && !hasScorecardAvailable) {
      const searchParams = new URLSearchParams(location.search)
      searchParams.delete('view')
      const newSearch = searchParams.toString()
      const queryString = newSearch ? `?${newSearch}` : ''
      history.replace(`${location.pathname}${queryString}`)
    }
  }, [query.view, hasScorecardAvailable, history, location.pathname, location.search])

  const contextValue = useMemo<PlaceReportsState>(
    () => ({
      ...placeManagerState,
      locale,
      selectedLocale,
      setLocale,
      setSelectedLocale,
      viewType,
      setViewType,
      reportsIndex,
      setReportsIndex,
      reportsIndexError,
      getRegion,
      getCountryAndRegion,
      getDivision,
      countries,
      hasScorecardAvailable,
    }),
    [
      placeManagerState,
      locale,
      selectedLocale,
      viewType,
      reportsIndex,
      setReportsIndex,
      reportsIndexError,
      getRegion,
      getCountryAndRegion,
      getDivision,
      countries,
      hasScorecardAvailable,
    ]
  )

  return (
    <PlaceReportsAppContext.Provider value={contextValue}>
      {placeManagerState?.permissionsError ? <Route component={NotFound} /> : children}
    </PlaceReportsAppContext.Provider>
  )
}

export function usePlaceReportsData(): PlaceReportsState {
  const cvAppData = useContext(PlaceReportsAppContext)
  if (cvAppData === undefined) {
    throw new TypeError('This hook must be used within a PlaceReportsPageProvider')
  }

  // If there's a permissions error, don't return sensitive data
  if (cvAppData.permissionsError) {
    return {
      permissionsError: cvAppData.permissionsError,
      locale: cvAppData.locale,
      selectedLocale: cvAppData.selectedLocale,
      setLocale: cvAppData.setLocale,
      setSelectedLocale: cvAppData.setSelectedLocale,
    } as PlaceReportsState
  }

  return cvAppData
}

export function usePlaceReportsPageData(): PlaceReportsState {
  return usePlaceReportsData()
}
